import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useAppLogo } from "../../../hooks/useAppLogo";
import {
  clearStoredAppLogo,
  setStoredAppLogo,
} from "../../../utils/appBranding";
import defaultLogo from "../../../assets/genie-logo.svg";
import { AvailableLogo, getAvailableLogo, uploadLogo } from "../../../api/api";
import "../admin.scss";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ScreenManagement: React.FC = () => {
  const storedLogo = useAppLogo();
  const [logoInput, setLogoInput] = useState(storedLogo ?? "");
  const [logoFileName, setLogoFileName] = useState("");
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [selectedLogoInfo, setSelectedLogoInfo] = useState<{
    fileName: string;
    fileSize: string;
    fileType: string;
    source: "Uploaded file" | "Available logo" | "Default logo";
    uploadedOn?: string;
    url?: string;
  }>({
    fileName: "Default Lipi logo",
    fileSize: "-",
    fileType: "SVG",
    source: "Default logo",
  });
  const [availableLogos, setAvailableLogos] = useState<AvailableLogo[]>([]);
  const [isSavingLogo, setIsSavingLogo] = useState(false);
  const [isLoadingLogos, setIsLoadingLogos] = useState(false);
  const [status, setStatus] = useState<string>("");
  const email = localStorage.getItem("Email") || "";
  const tenantId = localStorage.getItem("TenantId") || "1";

  useEffect(() => {
    setLogoInput(storedLogo ?? "");
  }, [storedLogo]);

  const previewLogo = useMemo(
    () => logoInput.trim() || storedLogo || defaultLogo,
    [logoInput, storedLogo],
  );

  const sanitizeLogoUrl = (value?: string | null) =>
    String(value || "")
      .replace(/^"+|"+$/g, "")
      .replace(/%22$/i, "");

  const formatBytes = (bytes: number) => {
    if (!bytes) return "0 KB";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      units.length - 1,
    );
    return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${
      units[index]
    }`;
  };

  const fetchLogos = async () => {
    if (!email) {
      setAvailableLogos([]);
      setStatus("Email not found. Please login again to manage logos.");
      return;
    }

    try {
      setIsLoadingLogos(true);
      const logos = await getAvailableLogo(email, tenantId);
      const normalizedLogos = logos.map((logo) => ({
        ...logo,
        blob_url: sanitizeLogoUrl(logo.blob_url),
      }));
      setAvailableLogos(normalizedLogos);
    } catch (error) {
      setAvailableLogos([]);
      setStatus("Unable to load available logos.");
    } finally {
      setIsLoadingLogos(false);
    }
  };

  useEffect(() => {
    fetchLogos();
  }, []);

  const handleLogoUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase() || "";
    const allowedExtensions = [
      "png",
      "jpg",
      "jpeg",
      "svg",
      "webp",
      "gif",
      "bmp",
      "ico",
      "avif",
    ];

    if (!file.type.startsWith("image/") && !allowedExtensions.includes(extension)) {
      setStatus("Please upload a valid image file.");
      event.target.value = "";
      return;
    }

    const localPreviewUrl = URL.createObjectURL(file);
    setSelectedLogoFile(file);
    setLogoInput(localPreviewUrl);
    setLogoFileName(file.name);
    setSelectedLogoInfo({
      fileName: file.name,
      fileSize: formatBytes(file.size),
      fileType: file.type || extension.toUpperCase(),
      source: "Uploaded file",
    });
    setStatus("Logo selected. Click Save Logo to upload and apply it.");
    event.target.value = "";
  };

  const handleSave = async () => {
    const nextValue = logoInput.trim();

    if (!selectedLogoFile && !nextValue) {
      setStatus("Choose a logo before saving.");
      return;
    }

    if (selectedLogoFile && !email) {
      setStatus("Email not found. Please login again to upload a logo.");
      return;
    }

    try {
      setIsSavingLogo(true);

      if (selectedLogoFile) {
        setStatus("Uploading logo...");
        const response = await uploadLogo(selectedLogoFile, email, "1", tenantId);
        const uploadedLogoUrl = sanitizeLogoUrl(response.image_blob_url);
        setLogoInput(uploadedLogoUrl);
        setStoredAppLogo(uploadedLogoUrl);
        setLogoFileName(response.file_name || selectedLogoFile.name);
        setSelectedLogoFile(null);
        setSelectedLogoInfo((prev) => ({
          ...prev,
          fileName: response.file_name || prev.fileName,
          source: "Available logo",
          url: uploadedLogoUrl,
        }));
        setStatus(response.message || "Logo uploaded and applied successfully.");
        await fetchLogos();
        return;
      }

      setStoredAppLogo(nextValue);
      setStatus("Logo updated successfully.");
    } catch (error) {
      setStatus("Logo upload failed. Please try again.");
    } finally {
      setIsSavingLogo(false);
    }
  };

  const handleReset = () => {
    clearStoredAppLogo();
    setLogoInput("");
    setLogoFileName("");
    setSelectedLogoFile(null);
    setSelectedLogoInfo({
      fileName: "Default Lipi logo",
      fileSize: "-",
      fileType: "SVG",
      source: "Default logo",
    });
    setStatus("Logo reset to default branding.");
  };

  const handleRemoveLogoFile = () => {
    setLogoInput("");
    setLogoFileName("");
    setSelectedLogoFile(null);
    setSelectedLogoInfo({
      fileName: "Default Lipi logo",
      fileSize: "-",
      fileType: "SVG",
      source: "Default logo",
    });
    setStatus("Logo file removed. Save changes or choose another logo.");
  };

  const handleUseAvailableLogo = (logo: AvailableLogo) => {
    const logoUrl = sanitizeLogoUrl(logo.blob_url);
    setLogoInput(logoUrl);
    setLogoFileName(logo.file_name);
    setSelectedLogoFile(null);
    setSelectedLogoInfo({
      fileName: logo.file_name,
      fileSize: "-",
      fileType: logo.file_name.split(".").pop()?.toUpperCase() || "Image",
      source: "Available logo",
      uploadedOn: logo.uploaded_on,
      url: logoUrl,
    });
    setStoredAppLogo(logoUrl);
    setStatus("Logo selected successfully.");
  };

  const UploadIcons = () => (
    <svg
      stroke="currentColor"
      fill="currentColor"
      strokeWidth="0"
      viewBox="0 0 24 24"
      height="1em"
      width="1em"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19ZM14 9V15H10V9H5L12 2L19 9H14Z"></path>
    </svg>
  );

  return (
    <div className="screen-management-page">
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="screen-title-header">
            {/* <h4>Change Logo</h4> */}
          </div>

          <Typography className="screen-management-subtitle">
            You can upload a new logo or paste a logo image URL, then save it to
            update the app header branding.
          </Typography>

          <Box className="screen-management-grid">
            <div className="screen-management-panel">
              <Typography className="screen-management-panel-title">
                Change Logo
              </Typography>

              <Stack spacing={2.5}>
                {/* <TextField
                  fullWidth
                  label="Logo Image URL"
                  placeholder="https://example.com/your-logo.png"
                  value={logoInput}
                  onChange={(event) => {
                    setLogoInput(event.target.value);
                    setLogoFileName("");
                  }}
                /> */}

                <div className="screen-management-upload">
                  <div className="upload-document-wrapper">
                    <Button
                      className="upload-label"
                      component="label"
                      variant="contained"
                      startIcon={<UploadIcons />}
                    >
                      Upload Logo
                      <VisuallyHiddenInput
                        type="file"
                        accept="image/*,.png,.jpg,.jpeg,.svg,.webp,.gif,.bmp,.ico,.avif"
                        onChange={handleLogoUpload}
                      />
                    </Button>

                    <div className="uploaded-file-wrappper">
                      {!logoFileName ? (
                        <div className="uploaded-file-wrappper__noFilesMesg">
                          There are no files to display!
                        </div>
                      ) : (
                        <ul className="uploaded-file-wrappper__uploadedFileList">
                          <li>
                            <span style={{ wordBreak: "break-word" }}>
                              {logoFileName}
                            </span>
                            <button
                              className="uploaded-file-wrappper__deleteIcon"
                              onClick={handleRemoveLogoFile}
                              title="Remove logo"
                              aria-label={`Remove logo ${logoFileName}`}
                              type="button"
                            >
                              <i>
                                <svg
                                  stroke="currentColor"
                                  fill="currentColor"
                                  strokeWidth="0"
                                  viewBox="0 0 24 24"
                                  height="100%"
                                  width="100%"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z"></path>
                                </svg>
                              </i>
                            </button>
                          </li>
                        </ul>
                      )}
                    </div>
                  </div>
                </div>

                <div className="screen-management-actions">
                  <Button
                    variant="outlined"
                    className="close-btn-usermgmt"
                    onClick={handleReset}
                  >
                    Reset Default
                  </Button>
                  <Button
                    variant="contained"
                    className="save-btn-usermgmt"
                    onClick={handleSave}
                    disabled={isSavingLogo}
                  >
                    {isSavingLogo ? "Saving..." : "Save Logo"}
                  </Button>
                </div>
              </Stack>
            </div>

            <div className="screen-management-preview">
              <Typography className="screen-management-panel-title">
                Preview
              </Typography>

              <div className="screen-management-preview-card">
                <div className="screen-management-preview-light">
                  <span>Light header</span>
                  <img src={previewLogo} alt="App logo preview" />
                </div>
                <div className="screen-management-preview-dark">
                  <span>Dark header</span>
                  <img src={previewLogo} alt="App logo preview dark" />
                </div>
              </div>

              <div className="screen-management-preview-data">
                <div>
                  <span>Source</span>
                  <strong>{selectedLogoInfo.source}</strong>
                </div>
                <div>
                  <span>File name</span>
                  <strong>{selectedLogoInfo.fileName}</strong>
                </div>
                <div>
                  <span>Type</span>
                  <strong>{selectedLogoInfo.fileType}</strong>
                </div>
                <div>
                  <span>Size</span>
                  <strong>{selectedLogoInfo.fileSize}</strong>
                </div>
                {selectedLogoInfo.uploadedOn && (
                  <div>
                    <span>Uploaded on</span>
                    <strong>{selectedLogoInfo.uploadedOn}</strong>
                  </div>
                )}
                {selectedLogoInfo.url && (
                  <div>
                    <span>URL</span>
                    <strong>{selectedLogoInfo.url}</strong>
                  </div>
                )}
              </div>
            </div>
          </Box>

          <div className="screen-management-logo-board">
            <Typography className="screen-management-panel-title">
              Available Logos
            </Typography>

            {isLoadingLogos ? (
              <div className="screen-management-logo-empty">
                Loading logos...
              </div>
            ) : availableLogos.length === 0 ? (
              <div className="screen-management-logo-empty">
                No logos uploaded yet.
              </div>
            ) : (
              <div className="screen-management-logo-items">
                {availableLogos.map((logo) => (
                  <div
                    className="screen-management-logo-item"
                    key={`${logo.file_name}-${logo.uploaded_on}`}
                  >
                    <div className="screen-management-logo-thumb">
                      <img src={logo.blob_url} alt={logo.file_name} />
                    </div>
                    <div className="screen-management-logo-meta">
                      <div>
                        <span>File name</span>
                        <strong>{logo.file_name}</strong>
                      </div>
                      <div>
                        <span>Uploaded</span>
                        <strong>{logo.uploaded_on}</strong>
                      </div>
                      <div>
                        <span>Status</span>
                        <strong>
                          {logo.is_active ? "Active" : "Available"}
                        </strong>
                      </div>
                      <div className="screen-management-logo-url">
                        <span>URL</span>
                        <strong>{logo.blob_url}</strong>
                      </div>
                      {logo.is_active && (
                        <em className="screen-management-logo-active">
                          Active
                        </em>
                      )}
                    </div>
                    <Button
                      variant="outlined"
                      className="close-btn-usermgmt"
                      onClick={() => handleUseAvailableLogo(logo)}
                    >
                      Use Logo
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {status && (
            <Alert severity="info" className="screen-management-alert">
              {status}
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreenManagement;
